import Axios from 'axios';
import * as Routes from '@/modules/routes';
import * as fabric from 'fabric';
import { centerImage } from '../functions';

interface LoadDataProps {
  answerId?: number;
  builder?: boolean;
  locale?: string;
  questionId: number;
  userType: UserType;
}

type QuestionAnswerResponse = {
  question_answer: {
    answer_data: string;
    answer_updated_at: string;
  };
};

type QuestionResponse = {
  question: {
    body?: string | undefined;
    data: {
      fabric_data: string;
    };
  };
};

type BackgroundImageResponse = {
  data: {
    height: number;
    width: number;
    image: {
      url: string;
      es_url: string;
    }
  }[];
};

interface LoadAnswerDataProps {
  answerId?: number;
  builder?: boolean;
  userType: UserType;
}

const loadAnswerData = async ({ answerId, builder = false, userType }: LoadAnswerDataProps) => {
  if (!answerId || builder || userType === 'Sysadmin') return null;

  const answerData = await Axios.get<QuestionAnswerResponse>(Routes.plato_api_question_answer_path(answerId));

  try {
    const parsedAnswerData = JSON.parse(answerData.data.question_answer.answer_data);

    return ({
      answerData: Object.fromEntries(
        Object.entries(parsedAnswerData).filter(([key]) => key !== 'backgroundImage' && key !== 'locale'),
      ),
      answerLocale: parsedAnswerData.locale,
      answerUpdatedAt: answerData.data.question_answer.answer_updated_at,
    });
  } catch (error) {
    return null;
  }
};

const loadQuestionData = async ({ locale, questionId }: { locale: string, questionId: number }) => (
  Axios.get<QuestionResponse>(Routes.plato_api_question_path(questionId ?? 0, { locale }))
);

// eslint-disable-next-line consistent-return
const loadBackgroundImage = async ({ locale, questionId }: { locale: string, questionId: number }) => {
  try {
    const backgroundImageData = await Axios.get<BackgroundImageResponse>(Routes.plato_api_image_models_path({
      background_images: true,
      for_model: { model_id: questionId, model_type: 'Question' },
      extra_attributes: ['height', 'width'],
      includes: ['image'],
    }));

    if (backgroundImageData.data.data.length > 0) {
      try {
        const { image } = backgroundImageData.data.data[0];
        const hasValidSpanishUrl = locale === 'es' && image.es_url && image.es_url !== '/missing.png';
        const imageUrl = hasValidSpanishUrl ? image.es_url : image.url;

        const fabricImage = await fabric.Image.fromURL(
          imageUrl,
          { crossOrigin: 'anonymous' },
        );

        centerImage(fabricImage);

        return {
          backgroundImageData: fabricImage,
          backgroundImageDimensions: {
            height: backgroundImageData.data.data[0].height,
            width: backgroundImageData.data.data[0].width,
          },
        };
      } catch (err) {
        // this may error if there is no image at the url
        console.debug(err);

        return {};
      }
    }
  } catch (error) {
    if (error.response.status === 404) {
      return {};
    }

    console.error(error);
  }

  return {};
};

const loadData = async ({ answerId, builder = false, locale = 'en', questionId, userType }: LoadDataProps) => {
  const answerData = await loadAnswerData({ answerId, builder, userType });
  const questionData = await loadQuestionData({ locale, questionId });

  // Use the locale stored in the answer data (student's locale at submission time)
  // instead of the viewer's locale, so background images render as the student saw them
  const backgroundLocale = answerData?.answerLocale || locale;
  const { backgroundImageData, backgroundImageDimensions } = await loadBackgroundImage({
    locale: backgroundLocale,
    questionId,
  });

  return ({
    ...answerData,
    backgroundImageData,
    backgroundImageDimensions,
    questionData,
  });
};

export default loadData;
